import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { debounceTime, distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostUnit } from '../../../models/cost-unit.model';
import { IPlannedSalePrice } from '../../../models/planned-sale-price.model';
import { TransactionalDataFacade } from '../../../services/facades/external-data/transactional-data-facade';
import { ITransactionalDataState } from '../../../services/facades/external-data/transactional-data.state.model';

@Component({
    templateUrl: './planned-sale-price.page.html',
    styleUrls: [ './planned-sale-price.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlannedSalePricePage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;
    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public dataSource: MatTableDataSource<IPlannedSalePrice> = new MatTableDataSource<IPlannedSalePrice>([]);
    public transactionalDataState$: Observable<ITransactionalDataState>;
    public datefilterFormGroup: FormGroup = new FormGroup({ start: new FormControl<Moment>(null), end: new FormControl<Moment>(null) });
    private destroy$: Subject<void> = new Subject();

    constructor(private myTransactionalDataFacade: TransactionalDataFacade) {
    }

    public ngOnInit(): void {
        this.transactionalDataState$ = this.myTransactionalDataFacade.subState();
        this.transactionalDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ITransactionalDataState) => s.plannedSalePrices),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: IPlannedSalePrice[]) => {
                this.dataSource.data = data;
            });
        this.datefilterFormGroup.valueChanges.pipe(debounceTime(500)).subscribe((daterange: { start: Moment, end: Moment }) => {
            if (!isNullOrUndefined(daterange.start) && !isNullOrUndefined(daterange.end)) {
                this.myTransactionalDataFacade.setDateRange(daterange);
                void this.myTransactionalDataFacade.fetchPlannedSalePrices();
            }
        });

        // Initialize the datepicker with the daterange from the state
        const state: ITransactionalDataState = this.myTransactionalDataFacade.getStateSnapshot();
        this.datefilterFormGroup.setValue(state.selectedDateRange);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Remove Sales from memory
        this.myTransactionalDataFacade.clearPlannedSalePrices();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.setupFilterAndSorting();
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    private setupFilterAndSorting(): void {
        this.transactionalDataState$.pipe(
            takeUntil(this.destroy$),
            map((state: ITransactionalDataState) => state.costUnitLookup),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        ).subscribe((costUnitLookup: Record<string, ICostUnit>) => {

            this.dataSource.sortingDataAccessor = (item: IPlannedSalePrice, tableColumnName: string): string | number => {
                switch (tableColumnName) {
                    case 'costUnit':
                        return item.articleNr in costUnitLookup ? costUnitLookup[ item.articleNr ].name : item.articleNr;
                    default:
                        return item[ tableColumnName ];
                }
            };

            this.dataSource.filterPredicate = (data: IPlannedSalePrice, filter: string): boolean => {
                filter = filter.trim().toLowerCase();
                const foundCostUnit: ICostUnit = costUnitLookup[ data.articleNr ];

                if (foundCostUnit !== undefined) {
                    return foundCostUnit.name.trim().toLowerCase().indexOf(filter) !== -1;
                }
                return data.articleNr.trim().toLowerCase().indexOf(filter) !== -1;
            };
        });
    }

}
